

import Vue from "vue";
import axios from "axios";
import {MODULE} from "@/config/modules";
import {AuthService, AWSFileService} from "@/services";
import {AWSFile} from "@/models/aws-file";
import {InboxreaderPurchase} from "@/utils/purchase/inboxreader-purchase";
import {InboxState} from "@/utils/inbox/inbox-state";
import AppCustomTable from "@/components/common/CustomTable.vue";
import AppToProcessFile from "./ToProcessFile.vue";
import AppToProcessFileAllFiles from "./ToProcessFileAllFiles.vue";
import { InboxreaderPurchaseFile } from "@/utils/purchase/inboxreader-purchase-file";
import AppOrderPreview from "./OrderPreview.vue";
import AppUploadDropTile from "./UploadDropTile.vue";
import { Order } from "@/utils/peppol/order";
import { parseUblXml } from "@/utils/peppol/ubl-xml-parser";
import { InboxTypeState } from "@/utils/inbox/inbox-type-state";
import { visibleFiles } from "@/utils/inbox/inbox-list-state";
import { dayKey } from "@/utils/inbox/dates";

export default Vue.extend({
	data() {
		return {
			open: false,
			dossierConfig: AuthService.wfDossier,
			allFilesOfPurchase: null as null | InboxreaderPurchase,
			toPreviewOrder: null as null | Order,
			filling: false
		};
	},
	props: {
		state: {
			type: Object as () => InboxState,
			required: true,
		},
		typeState: {
			type: Object as ()=>InboxTypeState,
			required: true,
		},
		grid: Boolean,
		canUpload: Boolean
	},
	computed: {
		moduleId(): number {
			return MODULE.INKOMENDE_AANKOPEN_UIT_MAILBOX;
		},
		hasModule(): boolean {
			return AuthService.hasModule(this.moduleId);
		},
		filteredPurchases(): InboxreaderPurchase[] {
			return this.typeState.purchases;
		},
		// dayGroups splits the list by the day the documents came in. The list
		// arrives sorted by that date, so one pass is enough.
		dayGroups(): {key:string, label:string, count:number, purchases:InboxreaderPurchase[]}[] {
			let groups:{key:string, label:string, count:number, purchases:InboxreaderPurchase[]}[] = [];
			for (let purchase of this.filteredPurchases) {
				let key = dayKey(purchase.DateCreated);
				let group = groups.length ? groups[groups.length-1] : null;
				if (!group || group.key != key) {
					group = {key, label: this.dayLabel(purchase.DateCreated), count: 0, purchases: []};
					groups.push(group);
				}
				group.purchases.push(purchase);
				group.count += visibleFiles(purchase, this.state.archive).length;
			}
			return groups;
		}
	},
	watch: {
		grid(){
			this.ensureFilled();
		},
		filteredPurchases(){
			this.ensureFilled();
		}
	},
	methods: {
		dayLabel(date:Date):string{
			if (dayKey(date) == dayKey(new Date())) return this.$ct("received-today").toString();
			return date.toLocaleDateString("nl-be", {day: "numeric", month: "long", year: "numeric"});
		},
		async previewFile(file:InboxreaderPurchaseFile){
			if (!file.File) return;

			// For a peppol document: if the file is XML, parse and show order preview
			let purchaseOfFile = this.findPurchaseByFile(file);
			if (purchaseOfFile && purchaseOfFile.PeppolOrder && this.isXmlFile(file.File)) {
				try {
					const order = await this.fetchAndParseXml(file.File);
					if (order) {
						this.toPreviewOrder = order;
						return;
					}
				} catch (e) {
					console.error("Failed to parse XML:", e);
					// Fall through to default preview behavior
				}
			}

			if (!file.File.shouldOpenInModal()) {
				AWSFileService.previewAwsFile(file.File);
			}else{
				let url = file.File.getUrl();
				window.open(url, "_blank");
			}
		},
		async fetchAndParseXml(file: AWSFile): Promise<Order | null> {
			const link = await AWSFileService.getLink(file.Token);
			const response = await axios.get(link, { responseType: "text" });
			const xmlString = response.data as string;
			return parseUblXml(xmlString);
		},
		isXmlFile(file: AWSFile): boolean {
			const name = file.Name.toLowerCase();
			return name.endsWith(".xml") || file.ContentType === "application/xml" || file.ContentType === "text/xml";
		},
		findPurchaseByFile(file: InboxreaderPurchaseFile): InboxreaderPurchase | undefined {
			return this.filteredPurchases.find(p => p.Files.some(f => f.ID === file.ID));
		},
		async downloadFile(file:InboxreaderPurchaseFile){
			if(!file.File?.Token) return;
			await AWSFileService.downloadFile(file.File?.Token,file.File?.Name);
		},
		onScroll(){
			let el = this.$refs.scrollEl as HTMLElement;
			if (!el) return;
			let distanceTillBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
			if (distanceTillBottom > 300) return;
			this.state.fetchMore();
		},
		async ensureFilled(){
			if (this.filling) return;
			this.filling = true;
			try{
				for (let i = 0; i < 50; i++){
					await this.$nextTick();
					let el = this.$refs.scrollEl as HTMLElement;
					if (!el) return;
					if (el.scrollHeight > el.clientHeight + 100) return;
					if (this.typeState.endOfDataReached()) return;
					await this.state.fetchMore();
					await new Promise(resolve=>setTimeout(resolve, 150));
				}
			}finally{
				this.filling = false;
			}
		}
	},
	mounted(){
		this.ensureFilled();
	},
	components: {
		AppCustomTable,
		AppToProcessFile,
		AppToProcessFileAllFiles,
		AppOrderPreview,
		AppUploadDropTile
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
